import { Button, Grid, ScrollArea, Stack, Text, Title } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { modals } from '@mantine/modals';
import { useEffect } from 'react';

import { Customer, CustomerPayments } from '@e-origin/shared';

import { AdditionalFiscalReferenceTable, LeavePageNotification, Select, StickyContainer, TextInput } from '@components';
import { EditCustomerType } from '@enums';
import { getMethodOfPaymentTypes } from '@utils';
import { useCustomerViewPaymentsFormInitializer } from './useCustomerViewPaymentsFormInitializer';

const methodOfPaymentTypes = getMethodOfPaymentTypes();

interface CustomerViewPaymentsProps {
  handleSubmitCustomer(type: EditCustomerType, childForm: UseFormReturnType<any>): void;
  handleUpdatePayments?: (updatedPayments: Customer['payments']) => void;
  customerDetails: Customer;
  isCreate: boolean;
  isButtonDisabled: boolean;
  isBE: boolean;
}

export const CustomerViewPayments: React.FC<CustomerViewPaymentsProps> = ({
  handleSubmitCustomer,
  handleUpdatePayments,
  isButtonDisabled,
  customerDetails,
  isCreate,
  isBE,
}) => {
  const { form, buildFormValues } = useCustomerViewPaymentsFormInitializer({ customerDetails });

  const handleUpdate = (updateData: CustomerPayments) => {
    handleUpdatePayments(updateData);
  };

  const toggleSavingPaymentsModal = () => {
    if (!form.values.payments.methodOfPayment || !form.values.payments.deferredPayment || (!form.values.payments.methodOfPaymentVAT && isBE)) {
      modals.openConfirmModal({
        title: 'Confirm saving payment details',
        children: <Text>Are you sure you want to save without any Deferred payment? Deferred payment is usually mandatory for Import.</Text>,
        labels: { confirm: 'Yes', cancel: 'No' },
        confirmProps: { color: 'red' },
        onConfirm: () => {
          handleSubmitCustomer(EditCustomerType.PAYMENTS, form);
        },
      });
    } else {
      handleSubmitCustomer(EditCustomerType.PAYMENTS, form);
    }
  };

  useEffect(() => {
    if (customerDetails) {
      form.setValues(buildFormValues(customerDetails));
      form.resetDirty({} as any);
    }
  }, [customerDetails]);

  return (
    <>
      <LeavePageNotification hasUnsavedChanges={form.isDirty()} />

      <ScrollArea h="100%" offsetScrollbars="y">
        <Stack mx="xl">
          <AdditionalFiscalReferenceTable
            handleUpdate={handleUpdate}
            isCustomerFlow={true}
            payments={form.values.payments}
            titleOrder={5}
            groupProps={{ my: 'xl', justify: 'space-between' }}
          />

          <Stack gap="md" mb="md">
            <Title order={5} fw={600} mb="md">
              Payment info
            </Title>

            <Grid>
              <Grid.Col span={4}>
                <Select {...form.getInputProps('payments.methodOfPayment')} label="Method of payment type" data={methodOfPaymentTypes} />
              </Grid.Col>
              <Grid.Col span={4}>
                <TextInput {...form.getInputProps('payments.deferredPayment')} label="Deferred payment" />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={4}>
                <Select {...form.getInputProps('payments.methodOfPaymentVAT')} label="Method of payment vat type" data={methodOfPaymentTypes} />
              </Grid.Col>
              <Grid.Col span={4}>
                <TextInput {...form.getInputProps('payments.deferredPaymentVAT')} label="Deferred payment vat" />
              </Grid.Col>
            </Grid>
          </Stack>

          <Stack gap="md" my="md">
            <Title order={5} fw={600} mb="md">
              Person paying the customs duty
            </Title>

            <Grid>
              <Grid.Col span={4}>
                <TextInput {...form.getInputProps('personPayingTheCustomDuty.identificationNumber')} label="Identification number" />
              </Grid.Col>
            </Grid>
          </Stack>
        </Stack>

        <StickyContainer>
          <Button disabled={isButtonDisabled || !form.isValid()} onClick={toggleSavingPaymentsModal} type="submit" variant="filled" size="lg">
            {isCreate ? 'Create' : 'Save'}
          </Button>
        </StickyContainer>
      </ScrollArea>
    </>
  );
};
